'use client';
import { FunctionComponent, useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import { GalleryImage } from '@xFrame4/business/images/ImageGallery';
import SmoothImage from '@xFrame4/components/common/SmoothImage';

export interface ImageGalleryCompProps
{
    /** The gallery images to display. */
    galleryImages: GalleryImage[];
    /** The class name to add to the container. */
    className?: string;
}

/**
 * Component: an image opens a lightbox with the image gallery.
 */
const ImageGalleryComp: FunctionComponent<ImageGalleryCompProps> = (props) =>
{
    const [isOpen, setIsOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    /** Open image gallery (lightgallery.js) */
    const openImageGallery = () =>
    {
        setCurrentIndex(0);
        setIsOpen(true);
    }

    /** The gallery images */
    const galleryImages = props.galleryImages.map((image) => ({
        src: image.original.url,
        thumbnail: image.thumbnail.url,
    }));

    /** The thumbnail image */
    let defaultImage = props.galleryImages.find(i => i.isDefault) ?? props.galleryImages[0] ?? undefined;

    /** Render */
    return (
        <div className={`image-gallery ${props.className ?? ''}`}>
            {defaultImage && (
                <div className="image-gallery-container position-relative cursor-pointer" onClick={() => openImageGallery()}>
                    <SmoothImage
                        src={defaultImage.veryLarge169.url}
                        width={defaultImage.veryLarge169.width}
                        height={defaultImage.veryLarge169.height}
                        className="image-gallery-default-image w-100"
                    />
                    <div className="image-gallery-overlay">
                        <div className="image-gallery-icon">
                            <i className="fas fa-expand-alt"></i>
                        </div>
                    </div>
                </div>
            )}

            {/* Lightbox with Thumbnails and Zoom */}
            {isOpen && (
                <Lightbox
                    open={isOpen}
                    close={() => setIsOpen(false)}
                    slides={galleryImages}
                    plugins={[Thumbnails, Zoom]}
                    index={currentIndex}
                    thumbnails={{ showToggle: true }}
                    zoom={{
                        maxZoomPixelRatio: 3,
                        zoomInMultiplier: 2,
                        doubleTapDelay: 300,
                        doubleClickDelay: 300,
                        doubleClickMaxStops: 2,
                        keyboardMoveDistance: 50,
                        wheelZoomDistanceFactor: 100,
                        pinchZoomDistanceFactor: 100,
                        scrollToZoom: true
                    }}
                />
            )}
        </div>
    );
}

export default ImageGalleryComp;